import {MessageContentFunctionCall} from "@/services/wingman";
import {
  extractCodePayload,
  extractExecutionOutput,
  extractPythonScript,
  extractShellCommand,
  getCodeExecutionKind,
  inferCommandSummary,
  inferPythonSummary,
  isFailedToolResult,
  normalizeWhitespace,
  parseToolResult,
  truncateText,
  type TranslateFn,
} from "@repo/ui/gailgpt/code-execution";

export type ToolCallStep = {
   data: MessageContentFunctionCall;
   isCompleted?: boolean;
   result?: unknown;
};

export {
  type CodeExecutionKind,
  type TranslateFn,
  normalizeWhitespace,
  truncateText,
  parseToolResult,
  isFailedToolResult,
  isPythonExecutionTool,
  isCodeExecutionTool,
  getCodeExecutionKind,
  inferCommandSummary,
  extractShellCommand,
  extractPythonScript,
  extractCodePayload,
  inferPythonSummary,
  extractExecutionOutput,
} from "@repo/ui/gailgpt/code-execution";

/** Highlight languages `buildCodeExecutionStep` can emit for the code block. */
export type StepCodeLanguage = "python" | "bash" | "json" | "text";

export type ExecutionState = "running" | "failed" | "completed";

/** Failure wins over completion: a failed result should never look done/quiet. */
export const getExecutionState = (hasFailure: boolean, isCompleted: boolean): ExecutionState =>
   hasFailure ? "failed" : isCompleted ? "completed" : "running";

export type CodeExecutionStepDetail = {
   id: string;
   title: string;
   badgeText: string;
   codeLanguage: StepCodeLanguage;
   code: string | null;
   output: string | null;
   outputLabel: string;
   failed: boolean;
   completed: boolean;
};


/**
 * When we can't find a known key for the code payload, stringify the
 * parameters so the user still sees what the model actually sent. Filters
 * out empty parameter objects to avoid rendering `{}`.
 */
const stringifyParametersAsCode = (parameters?: Record<string, unknown>): string | null => {
   if (!parameters || Object.keys(parameters).length === 0) return null;
   try {
      return JSON.stringify(parameters, null, 2);
   } catch {
      return null;
   }
};

type StepContext = {
   data: MessageContentFunctionCall;
   parsedResult: Record<string, unknown> | null;
   failed: boolean;
   isCompleted: boolean;
   t: TranslateFn;
};

const buildStepDetail = (
   ctx: StepContext,
   detail: {
      title: string;
      badgeText: string;
      codeLanguage: StepCodeLanguage;
      code: string | null;
   }
): CodeExecutionStepDetail => ({
   id: ctx.data.callId,
   title: detail.title,
   badgeText: detail.badgeText,
   codeLanguage: detail.codeLanguage,
   code: detail.code,
   output: extractExecutionOutput(ctx.parsedResult),
   outputLabel: ctx.failed ? ctx.t("tools.labels.error") : ctx.t("tools.labels.output"),
   failed: ctx.failed,
   completed: !!ctx.isCompleted,
});

const buildPythonStepDetail = (ctx: StepContext): CodeExecutionStepDetail => {
   const script = extractPythonScript(ctx.data.parameters);

   return buildStepDetail(ctx, {
      title: script ? (inferPythonSummary(script, ctx.t) ?? ctx.t("tools.phrases.ranPython")) : ctx.t("tools.phrases.ranPython"),
      badgeText: ctx.t("tools.labels.python"),
      codeLanguage: script ? "python" : "json",
      code: script ?? stringifyParametersAsCode(ctx.data.parameters),
   });
};

const buildCodePayloadStepDetail = (ctx: StepContext): CodeExecutionStepDetail => {
   const script = extractCodePayload(ctx.data.parameters);

   return buildStepDetail(ctx, {
      title: script ? truncateText(normalizeWhitespace(script.split(/\r?\n/u)[0] ?? "")) : ctx.t("tools.phrases.ranCode"),
      badgeText: ctx.t("tools.labels.code"),
      // No dedicated grammar bundled for arbitrary languages — plain text.
      codeLanguage: script ? "text" : "json",
      code: script ?? stringifyParametersAsCode(ctx.data.parameters),
   });
};

const buildShellStepDetail = (ctx: StepContext): CodeExecutionStepDetail => {
   const command = extractShellCommand(ctx.data.parameters);

   return buildStepDetail(ctx, {
      title: command ? inferCommandSummary(command, ctx.t) : ctx.t("tools.phrases.ranShellCommand"),
      badgeText: ctx.t("tools.labels.shell"),
      codeLanguage: command ? "bash" : "json",
      code: command ?? stringifyParametersAsCode(ctx.data.parameters),
   });
};

export const buildCodeExecutionStep = (
   step: ToolCallStep,
   t: TranslateFn
): CodeExecutionStepDetail => {
   const {data, result, isCompleted} = step;
   const kind = getCodeExecutionKind(data.functionName ?? "", data.parameters);
   const parsedResult = parseToolResult(result);

   const ctx: StepContext = {
      data,
      parsedResult,
      failed: isFailedToolResult(parsedResult),
      isCompleted: !!isCompleted,
      t,
   };

   if (kind === "python") return buildPythonStepDetail(ctx);
   if (kind === "code") return buildCodePayloadStepDetail(ctx);
   return buildShellStepDetail(ctx);
};
